import {
  BRANDING_COLOR_DEFAULTS,
  CHALLENGE_ICON_TYPE_LABELS,
  CHALLENGE_ICON_TYPES,
  HEX_COLOR_REGEX,
  THEME_OPTIONS,
  hasAnyBranding,
  type BrandingFormData,
  type ChallengeIconType,
} from '@/lib/eventBranding';
import { ColorSwatchPicker } from '@/components/shared/ColorSwatchPicker';
import {
  ALL_MAP_ICON_NAMES,
  CHALLENGE_TYPE_MAP_DEFAULTS,
  MAP_ICON_REGISTRY,
  formatIconName,
  resolveMapIcon,
} from '@/lib/mapIconRegistry';
import { buildMediaUrl } from '@/lib/media';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  ImageUploadDropzone,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@startupquickstart/frontend';
import { HelpCircle, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';

/** Update a single branding field by key. */
type BrandingFieldUpdater = <K extends keyof BrandingFormData>(
  key: K,
  value: BrandingFormData[K],
) => void;

interface BrandingFieldsProps {
  value: BrandingFormData;
  errors: Record<string, string>;
  onChange: BrandingFieldUpdater;
}

/**
 * The Branding + Challenge Map Icons editor sections, driven entirely by props.
 *
 * Stateless so it can be hosted by the event's Branding tab (which owns the
 * form state + save). Renders two cards: brand identity (logos, colors, theme,
 * label, display toggles) and per-challenge-type map-icon overrides.
 *
 * @param props - Current branding values, validation errors, and an updater.
 */
export function BrandingFields({ value, errors, onChange }: BrandingFieldsProps) {
  return (
    <div className="space-y-6">
      {/* Branding — logo + colors + theme apply on join in both mobile and the
          leaderboard display. Each field is optional; clients fall back to
          default tokens when blank. */}
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b">
          <CardTitle>Branding</CardTitle>
          <CardDescription>
            Logo, colors, and theme applied on the mobile app and the leaderboard display
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <ImageUploadDropzone
              targetAspectRatio={1}
              value={value.logoId}
              onChange={(mediaId) => onChange('logoId', mediaId)}
              label="Event Logo"
            />
            <ImageUploadDropzone
              targetAspectRatio={211 / 70}
              value={value.logoSmallId}
              onChange={(mediaId) => onChange('logoSmallId', mediaId)}
              label="Footer Logo (landscape)"
            />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <ColorField
              id="backgroundColor"
              label="Background Color"
              description="App background tint — leave blank for the default"
              value={value.backgroundColor}
              onChange={(v) => onChange('backgroundColor', v)}
              error={errors.backgroundColor}
              defaultColor={BRANDING_COLOR_DEFAULTS.background}
            />
            <ColorField
              id="accentColor"
              label="Accent Color"
              description="Buttons + highlights — leave blank for the default"
              value={value.accentColor}
              onChange={(v) => onChange('accentColor', v)}
              error={errors.accentColor}
              defaultColor={BRANDING_COLOR_DEFAULTS.accent}
            />
          </div>

          <FormFieldGroup name="theme" label="Theme" description="Visual preset applied across the app">
            <Select value={value.theme} onValueChange={(v) => onChange('theme', v)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a theme" />
              </SelectTrigger>
              <SelectContent>
                {THEME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormFieldGroup>

          <FormFieldGroup
            name="customPointsLabel"
            label="Custom Points Label"
            description='Renames "Points" everywhere — e.g. "Doubloons" for a pirate-themed hunt'
          >
            <Input
              id="customPointsLabel"
              value={value.customPointsLabel}
              onChange={(e) => onChange('customPointsLabel', e.target.value)}
              placeholder="Points"
              maxLength={50}
            />
          </FormFieldGroup>

          <div className="flex items-start justify-between gap-6 rounded-lg border bg-muted/20 p-4">
            <div>
              <p className="text-sm font-medium text-foreground">Hide POI Numbers on Map</p>
              <p className="text-sm text-muted-foreground">
                Hide the index numbers on point-of-interest markers. Purely cosmetic — challenges
                can always be completed in any order regardless of this setting.
              </p>
            </div>
            <Switch
              checked={value.hidePoiOrder}
              onCheckedChange={(checked) => onChange('hidePoiOrder', checked)}
            />
          </div>

          {/* DT-157. Map-pin tap-to-activate. Off by default so existing
              events keep their current flow. */}
          <div className="flex items-start justify-between gap-6 rounded-lg border bg-muted/20 p-4">
            <div>
              <p className="text-sm font-medium text-foreground">Tap Map Pin to Activate</p>
              <p className="text-sm text-muted-foreground">
                Lets teams activate a challenge by tapping its pin on the map. Off by default; teams
                open the challenge from the list and use its activation flow instead.
              </p>
            </div>
            <Switch
              checked={value.permitActivateOnPin}
              onCheckedChange={(checked) => onChange('permitActivateOnPin', checked)}
            />
          </div>

          {/* DT-166. Slideshow timing reuses the event's background colour +
              logo, so the only knob is how long to hold each photo. */}
          <div className="flex items-start justify-between gap-6 rounded-lg border bg-muted/20 p-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">Photo Slideshow Interval</p>
              <p className="text-sm text-muted-foreground">
                Seconds each photo stays on screen during the post-event slideshow. The slideshow
                runs in the Display View at <span className="font-mono">/display?mode=slideshow</span>.
              </p>
            </div>
            <Input
              type="number"
              min={2}
              max={60}
              className="w-24"
              value={value.slideshowIntervalSeconds}
              onChange={(e) =>
                onChange(
                  'slideshowIntervalSeconds',
                  Math.max(2, Math.min(60, parseInt(e.target.value) || 5)),
                )
              }
            />
          </div>

          {/* Bulk reset — revert every branding field at once. Renders only
              when at least one field is set, so unchanged forms stay quiet. */}
          {hasAnyBranding(value) && (
            <div className="flex justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onChange('logoId', undefined);
                  onChange('logoSmallId', undefined);
                  onChange('backgroundColor', '');
                  onChange('accentColor', '');
                  onChange('theme', 'default');
                  onChange('customPointsLabel', '');
                  onChange('hidePoiOrder', false);
                  onChange('challengeTypeIcons', {});
                }}
              >
                <RotateCcw className="h-4 w-4" />
                Reset Branding to Default
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Challenge Map Icons (DT-165 phase B). One row per challenge type with
          an optional custom icon + color. Mobile resolves: per-challenge
          override → these defaults → built-in type colors. Empty rows are
          pruned on save. */}
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b">
          <CardTitle>Challenge Map Icons</CardTitle>
          <CardDescription>
            Override the map pin icon and color per challenge type. Per-challenge overrides still
            win — these are the defaults for everything else.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {CHALLENGE_ICON_TYPES.map((type) => {
              const entry = value.challengeTypeIcons[type] ?? {};
              const colorError = errors[`typeIconColor.${type}`];
              const label = CHALLENGE_ICON_TYPE_LABELS[type];
              const defaults = CHALLENGE_TYPE_MAP_DEFAULTS[type];
              const isCustomized = Boolean(entry.iconId || entry.icon || entry.color?.trim());
              const updateEntry = (next: { iconId?: string; icon?: string; color?: string }) => {
                onChange('challengeTypeIcons', {
                  ...value.challengeTypeIcons,
                  [type]: { ...entry, ...next },
                });
              };
              return (
                <div key={type} className="rounded-lg border bg-muted/10 p-3">
                  <div className="flex items-start gap-3">
                    <MapIconPickerButton
                      label={label}
                      type={type}
                      entry={entry}
                      onSelectIcon={(name) => updateEntry({ icon: name, iconId: '' })}
                      onUploadImage={(mediaId) =>
                        updateEntry(mediaId ? { iconId: mediaId, icon: '' } : { iconId: '' })
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">{label}</p>
                          {isCustomized && <p className="text-xs text-muted-foreground">Custom</p>}
                        </div>
                        {isCustomized && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="-mr-1 -mt-1 h-7 px-2 text-xs"
                            onClick={() => updateEntry({ iconId: '', icon: '', color: '' })}
                            aria-label={`Reset ${label} to default`}
                            title="Reset to default"
                          >
                            Reset
                          </Button>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <ColorSwatchPicker
                          ariaLabel={`${label} color swatch`}
                          value={entry.color ?? ''}
                          onChange={(color) => updateEntry({ color })}
                          className="h-8 w-8"
                          placeholderColor={defaults.color}
                        />
                        <Input
                          value={entry.color ?? ''}
                          onChange={(e) => updateEntry({ color: e.target.value })}
                          placeholder={defaults.color}
                          className={cn('h-8 flex-1 font-mono text-xs', colorError && 'border-destructive')}
                        />
                      </div>
                      {colorError && <p className="mt-1 text-xs text-destructive">{colorError}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Hex-color input paired with a native color-picker swatch. The text input is
 * the source of truth (paste/clear); the swatch is a visual convenience. Empty
 * means "use the default token" — the parent serialises that as null.
 */
function ColorField({
  id,
  label,
  description,
  value,
  onChange,
  error,
  defaultColor,
}: {
  id: string;
  label: string;
  description?: string;
  value: string;
  onChange: (next: string) => void;
  error?: string;
  /** The color mobile actually uses when this field is blank. */
  defaultColor: string;
}) {
  const hasValue = value.trim().length > 0;
  return (
    <FormFieldGroup name={id} label={label} description={description} error={error}>
      <div className="flex items-center gap-2">
        <ColorSwatchPicker
          ariaLabel={`${label} swatch`}
          value={value}
          onChange={onChange}
          placeholderColor={defaultColor}
        />
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={defaultColor}
          className={cn('flex-1 font-mono', error && 'border-destructive')}
        />
        {hasValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange('')}
            aria-label={`Reset ${label} to default`}
            title="Reset to default"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        )}
      </div>
    </FormFieldGroup>
  );
}

/**
 * Map-icon tile + picker for one challenge type. The tile previews exactly
 * what the mobile pin will show — custom image, else the selected (or
 * default) lucide icon on the effective pin color. The popover offers the
 * full lucide catalog (searchable — the same names mobile renders) with a
 * custom image upload as the escape hatch.
 */
function MapIconPickerButton({
  label,
  type,
  entry,
  onSelectIcon,
  onUploadImage,
}: {
  label: string;
  type: ChallengeIconType;
  entry: { iconId?: string; icon?: string; color?: string };
  onSelectIcon: (name: string) => void;
  onUploadImage: (mediaId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const defaults = CHALLENGE_TYPE_MAP_DEFAULTS[type];
  // The full catalog is ~1,600 icons; render on demand — the default icon
  // plus the first screenfuls, with search narrowing the rest.
  const visibleNames = useMemo(() => {
    const query = search.trim().toLowerCase().replace(/[\s-]/g, '');
    const matches = query
      ? ALL_MAP_ICON_NAMES.filter((n) => n.toLowerCase().includes(query))
      : ALL_MAP_ICON_NAMES;
    return matches.slice(0, 120);
  }, [search]);
  const imageUrl = entry.iconId ? buildMediaUrl(entry.iconId) : null;
  const TileIcon = resolveMapIcon(entry.icon) ?? resolveMapIcon(defaults.icon) ?? HelpCircle;
  const tileColor =
    entry.color && HEX_COLOR_REGEX.test(entry.color.trim()) ? entry.color.trim() : defaults.color;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label} icon`}
          title={entry.icon || entry.iconId ? undefined : `Default: ${defaults.icon}`}
          className="flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border transition-shadow hover:ring-2 hover:ring-ring/30"
          style={{ backgroundColor: tileColor }}
        >
          {imageUrl ? (
            <img src={imageUrl} alt="" className="h-8 w-8 object-contain" />
          ) : (
            <TileIcon size={22} className="text-white" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[19.5rem] p-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${ALL_MAP_ICON_NAMES.length} icons…`}
          className="mb-2 h-8 text-sm"
          autoFocus
        />
        <div className="grid max-h-56 grid-cols-8 content-start gap-1 overflow-y-auto pr-1">
          {visibleNames.map((name) => {
            const IconOption = MAP_ICON_REGISTRY[name];
            if (!IconOption) return null;
            const isTypeDefault = name === defaults.icon;
            const selected = entry.icon ? entry.icon === name : !entry.iconId && isTypeDefault;
            return (
              <button
                key={name}
                type="button"
                aria-label={formatIconName(name)}
                title={isTypeDefault ? `${formatIconName(name)} (type default)` : formatIconName(name)}
                onClick={() => {
                  onSelectIcon(name);
                  setOpen(false);
                }}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                  selected && 'border-ring bg-muted text-foreground',
                )}
              >
                <IconOption size={18} />
              </button>
            );
          })}
          {visibleNames.length === 0 && (
            <p className="col-span-8 py-4 text-center text-xs text-muted-foreground">
              No icons match “{search}”
            </p>
          )}
        </div>
        <div className="mt-3 border-t pt-3">
          <p className="mb-2 text-xs text-muted-foreground">Or upload a custom image</p>
          <ImageUploadDropzone
            compact
            label=""
            targetAspectRatio={1}
            value={entry.iconId || undefined}
            onChange={(mediaId) => {
              onUploadImage(mediaId || null);
              if (mediaId) setOpen(false);
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
